import { useCallback, useEffect, useRef } from 'react';
import {
  buildPersistedPostLayout,
  parsePersistedPostLayout,
  type PersistedPostLayoutBlob,
} from '@/lib/layouts/persistPostLayout';
import { stableSerialize } from './use-auto-save-subtitle-config';
import { useFlushOnPageHide } from './use-flush-on-page-hide';

interface Options {
  /** The live layout blob (built from editor state), or null for no layout. */
  blob: PersistedPostLayoutBlob | null;
  /** The raw `post_layout` the project loaded with — seeds the diff baseline
   *  so the load echo is never written back. */
  loadedBlob: unknown;
  onPersist?: (blob: PersistedPostLayoutBlob | null) => void;
  delay?: number;
}

type PendingWrite = { blob: PersistedPostLayoutBlob | null; serialized: string };

/** Normalizes a stored blob the same way the editor rebuilds it (sanitized
 *  binds, `field_binds` omitted when empty), so an untouched layout compares
 *  equal to what was loaded. */
function normalizeLoaded(value: unknown): PersistedPostLayoutBlob | null {
  const parsed = parsePersistedPostLayout(value);
  return parsed ? buildPersistedPostLayout(parsed) : null;
}

/** Debounced persistence of the post-frame layout. Same invariants as the
 *  other editor writers: only a real change is written (never the value just
 *  loaded or just saved), and a pending write is flushed on unmount, never
 *  dropped. */
export function useAutoSavePostLayout({ blob, loadedBlob, onPersist, delay = 400 }: Options) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;

  // Seed once from the loaded value (or its absence).
  if (lastSavedRef.current === null) {
    lastSavedRef.current = stableSerialize(normalizeLoaded(loadedBlob));
  }

  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || !onPersistRef.current) return;
    lastSavedRef.current = pending.serialized;
    onPersistRef.current(pending.blob);
  }, []);

  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);

  useEffect(() => {
    if (!onPersist) return;
    const serialized = stableSerialize(blob);
    if (serialized === lastSavedRef.current) {
      // Nothing to save, and any pending write is stale (an edit undone
      // before its debounce fired).
      pendingRef.current = null;
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { blob, serialized };
    timeoutRef.current = setTimeout(firePending, delay);
    // Clears only the timer; pendingRef survives for the unmount flush.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [blob, onPersist, delay, firePending]);

  // Unmount: flush, never drop.
  useEffect(() => () => firePending(), [firePending]);
}
